import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { type FieldValues, type Resolver, useForm } from 'react-hook-form';
import { productFormSchema } from '../../schemas/product';
import type {
  LocalizedStringArrayInput,
  LocalizedStringInput,
  ProductFormData,
  ProductSpecifications,
} from '../../types/product';
import {
  emptyLocalizedStringArray,
  normalizeLocalizedString,
  normalizeLocalizedStringArray,
} from './helpers';

export type LanguageCode = 'pt-BR' | 'en-US' | 'es-ES';

export const LANGUAGES = [
  { code: 'pt-BR' as const, label: 'Português (Brasil)' },
  { code: 'en-US' as const, label: 'English (US)' },
  { code: 'es-ES' as const, label: 'Español' },
] as const;

const DEFAULT_VALUES = {
  category: 'new-drones',
  name: { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
  subtitle: { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
  price: 0,
  image: '',
  images: [] as string[],
  description: { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
  rating: 0,
  inStock: true,
  featured: false,
  status: 'active',
  specifications: [] as ProductSpecifications,
  features: { 'pt-BR': [] as string[], 'en-US': [] as string[], 'es-ES': [] as string[] },
  condition: { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
  warranty: { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
  shipping: { 'pt-BR': '', 'en-US': '', 'es-ES': '' },
  video: '',
};

interface UseProductFormProps {
  initialData?: Partial<ProductFormData>;
  onSubmit: (data: ProductFormData) => void | Promise<void>;
}

export function useProductForm({ initialData, onSubmit }: UseProductFormProps) {
  const [activeLang, setActiveLang] = useState<LanguageCode>('pt-BR');

  const form = useForm<FieldValues>({
    resolver: zodResolver(productFormSchema) as unknown as Resolver<FieldValues>,
    defaultValues: initialData ? { ...DEFAULT_VALUES, ...initialData } : DEFAULT_VALUES,
    mode: 'onSubmit',
  });

  const {
    watch,
    setValue,
    formState: { errors },
  } = form;

  // Valores observados usando watch sem argumento para pegar todos os valores
  const watchedValues = watch();
  const specifications = (watchedValues.specifications as ProductSpecifications) || [];
  const features =
    (watchedValues.features as LocalizedStringArrayInput) || emptyLocalizedStringArray;

  // Log de erros para debug
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('Erros de validação:', errors);
    }
  }, [errors]);

  /**
   * Processa os dados do formulário e normaliza para ProductFormData
   */
  const handleFormSubmit = async (data: FieldValues) => {
    try {
      const images = data.images as string[];

      const formData: ProductFormData = {
        category: data.category as ProductFormData['category'],
        name: normalizeLocalizedString(data.name as LocalizedStringInput),
        subtitle: data.subtitle
          ? normalizeLocalizedString(data.subtitle as LocalizedStringInput)
          : undefined,
        price: data.price as number | undefined,
        image: images[0] || '',
        images: images,
        description: normalizeLocalizedString(data.description as LocalizedStringInput),
        rating: data.rating as number | undefined,
        inStock: data.inStock as boolean,
        features: data.features
          ? normalizeLocalizedStringArray(data.features as LocalizedStringArrayInput)
          : undefined,
        condition: data.condition
          ? normalizeLocalizedString(data.condition as LocalizedStringInput)
          : undefined,
        specifications: normalizeSpecifications(
          data.specifications as ProductSpecifications | undefined
        ),
        warranty: data.warranty
          ? normalizeLocalizedString(data.warranty as LocalizedStringInput)
          : undefined,
        shipping: data.shipping
          ? normalizeLocalizedString(data.shipping as LocalizedStringInput)
          : undefined,
        video: data.video as string | undefined,
        featured: data.featured as boolean,
        status: data.status as ProductFormData['status'],
      };

      await onSubmit(formData);
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
    }
  };

  /**
   * Normaliza especificações garantindo LocalizedString completo
   */
  function normalizeSpecifications(
    specs: ProductSpecifications | undefined
  ): ProductSpecifications | undefined {
    if (!specs || specs.length === 0) return undefined;

    return specs.map((spec) => ({
      id: spec.id,
      label: normalizeLocalizedString(spec.label as LocalizedStringInput),
      value: normalizeLocalizedString(spec.value as LocalizedStringInput),
      order: spec.order,
    }));
  }

  /**
   * Garante que um valor LocalizedStringInput tenha valores padrão para exibição
   */
  const ensureLocalizedValue = (value: LocalizedStringInput | undefined) => {
    return normalizeLocalizedString(value);
  };

  /**
   * Atualiza especificações no formulário
   */
  const setSpecifications = (specs: ProductSpecifications) => {
    setValue('specifications', specs);
  };

  /**
   * Atualiza features no formulário
   */
  const setFeatures = (newFeatures: LocalizedStringArrayInput) => {
    setValue('features', newFeatures);
  };

  return {
    form,
    control: form.control,
    handleSubmit: form.handleSubmit,
    errors,
    activeLang,
    setActiveLang,
    specifications,
    features: normalizeLocalizedStringArray(features),
    setSpecifications,
    setFeatures,
    handleFormSubmit,
    ensureLocalizedValue,
  };
}
